import React, {useEffect, useContext, useMemo} from 'react';
import { Box, Button, Stack, TableCell, TextField, MenuItem, Typography, IconButton, Dialog, Backdrop, CircularProgress, Snackbar } from '@mui/material';
import {Context} from '../App';
import SorTableCheckable from '../components/ui/sortable-checkable';
import { Add, AttachEmail, DeleteForever, Edit, InfoOutlined } from '@mui/icons-material';
import moment from 'moment';
import AppLayout from '../components/layout/AppLayout';
import AdmPaymentInfo from '../components/sections/AdmPaymentInfo';
import AdmPaymentForm from '../components/forms/admPayment';
import AdmPaymentEdit from '../components/forms/admPaymentEdit';

export default function AdmPayments() {
    const {setIsConnected} = useContext(Context)
    const user = JSON.parse(sessionStorage.getItem('user'))

    const [load, setLoad] = React.useState(true);
    const [open, setOpen] = React.useState(false);
    const [action, setAction] = React.useState('');
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackMsg, setSnackMsg] = React.useState('');

    const [payments, setPayments] = React.useState([]);
    const [payment, setPayment] = React.useState();
    const [selectItems, setItems] = React.useState([]);
    const [searchInput, setSearchInput] = React.useState('');
    const [statutFilter, setStatutFilter] = React.useState('');

    const table = [
        {header: "Demande", sortable: true, sortBy:(el)=>el.num, row: (row, index)=><TableCell key={index} style={{minWidth:'60px'}}>{row.num}</TableCell>},
        {header: "Date", sortable: true, sortBy:(el)=>new Date(el.createdAt).getTime(), row: (row, index)=><TableCell key={index} sx={{minWidth:'70px', textAlign:'center'}}>{moment(row.createdAt).format('DD/MM/yyyy')}</TableCell>},
        (
            user.role==='admin' ? {header: "Évènement", sortable: true, sortBy:(el)=>el.event?.titre.toLowerCase(), row: (row, index)=><TableCell key={index} style={{minWidth:'120px'}}>{row.event?.titre}</TableCell>}
            :
            {header: "Bénéficiaire", sortable: true, sortBy:(el)=>(el.user?.nom+' '+el.user?.prenom).toLowerCase(), row: (row, index)=><TableCell key={index} style={{textTransform:'capitalize', minWidth:'200px'}}>{row.user? (row.user.nom+' '+row.user.prenom): ''}</TableCell>}
        ),
        {header: "Status", sortable: true, sortBy:(el)=>el.statut.toLowerCase(), row: (row, index)=><TableCell key={index} sx={{minWidth:'150px', textAlign:'center'}}>{row.statut}</TableCell>},
        {header: "Montant", sortable: true, sortBy:(el)=>el.amount, row: (row, index)=><TableCell key={index} style={{minWidth:'80px'}}>{row.amount?.toFixed(2)} €</TableCell>},
        {header: "", sortable: false, row: (row, index)=><TableCell key={index} sx={{minWidth:'180px', textAlign:'end'}} >
            <IconButton aria-label="informations complémentaires" onClick={()=>{setPayment(row); manage_dialog(true, 'info')}}><InfoOutlined fontSize="small"/></IconButton>
            <IconButton aria-label="modifier" onClick={()=>{setPayment(row); manage_dialog(true, 'edit')}}><Edit fontSize="small"/></IconButton>
        </TableCell>}
    ]

    function normalize(str) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s-.€]/gi,'').toLowerCase()
    }

    function get_searchable(item) {
        return user.role==='admin' ? (item.num+item.event.titre) : (item.user.nom+' '+item.user.prenom+' '+item.num)
    }

    function searchFilter(a, b) {
        return normalize(get_searchable(a)).includes(normalize(b))
    }

    function manage_dialog(status, action='') {
        setOpen(status)
        setAction(action)
        if(!status) {
            setPayment()
        }
    }

    function manage_snackbar(status, msg='') {
        setOpenSnackbar(status)
        setSnackMsg(msg)
        if(!status) {
            setSnackMsg('')
        }
    }

    useEffect(()=>{
        if(user.role !== 'super admin' || payments.length<=0 || payments.every(p=> p.statut !== "demande envoyée")) return

        setLoad(true);
        const toUpdateStatut = payments.filter(p => p.statut === "demande envoyée")
        const query = {_id: {$in: toUpdateStatut.map(p => p._id)}, statut: 'demande envoyée'}  
        const update = {statut: 'en cours de traitement'}
        
        fetch(process.env.REACT_APP_API_URL.concat('/adm/payments'), {
            method: 'PATCH',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            },body: JSON.stringify({query, update})
        }).then(async response => {
            const token = response.headers.get('x-auth-token');
            if (response.status === 401) {
                sessionStorage.clear();
                setIsConnected(false);
            } else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                }
                if (response.status === 200) {
                    const json = await response.json();
                    setPayments(old => old.map(p => toUpdateStatut.find(t => t._id === p._id) ? {...p, statut: 'en cours de traitement'} : p));
                    manage_snackbar(true, `Statut de ${json.length} demande(s) de paiement mise(s) à jour`);
                }
            }
        }).catch(error => {
            console.error("Error updating payments:", error);
        }).finally(() => {
            setLoad(false);
        });
    }, [payments])

    useEffect(() => {
        if (!user || user.role === 'user') {
            sessionStorage.clear();
            setIsConnected(false);
        }

        const query = user.role==='admin' ? {user: user._id} : {statut: {$ne: 'terminée'}};

        fetch(process.env.REACT_APP_API_URL.concat('/adm/payments'), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'authorization': 'Bearer ' + sessionStorage.getItem('token')
            }, body: JSON.stringify(query)
        }).then(async response => {
            const token = response.headers.get('x-auth-token');
            if (response.status === 401) {
                sessionStorage.clear();
                setIsConnected(false);
            } else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                }
                if (response.status === 200) {
                    const json = await response.json();
                    setPayments(json);
                }
            }
        }).catch(error => {
            console.error("Error fetching payments:", error);
        }).finally(() => {
            setLoad(false);
        });
    }, []);

    return(
        <AppLayout>
            <Box flex={1} display='flex' flexDirection='column' sx={{padding:2}}>

                <Typography variant='h2' marginBlock={3} align='center'>Demandes de paiements</Typography>

                {user.role==='admin' && user.events.length > 0 && <Button variant="outlined" color="focus" onClick={()=>manage_dialog(true, 'add')} startIcon={<Add/>} sx={{marginBottom:2, alignSelf:'end', borderColor:'focus.main'}}>Nouvelle demande</Button> }

                <Stack direction="row" spacing={2} justifyContent='space-between' marginBottom={1.5} alignItems='center' >
                    <TextField value={searchInput} onChange={(e)=>setSearchInput(e.target.value)} label="Rechercher un paiement..." variant="outlined" size="small" sx={{width:'60%'}}/>
                    <TextField select label="Filtre" value={statutFilter} onChange={(e)=>setStatutFilter(e.target.value)} size="small" sx={{minWidth:'120px', width:'20%'}} slotProps={{ select: { native: true }, inputLabel:{shrink: true} }}>
                        <option value="">Tous</option>
                        <option value="demande envoyée">Demande envoyée</option>
                        <option value="en cours de traitement">En cours de traitement</option>
                        <option value="paiement effectué">Paiement effectué</option>
                        <option value="terminée">Terminée</option>
                        {user.role==='super admin' && <option value="annulée">Annulée</option>}
                    </TextField>
                </Stack>

                <SorTableCheckable {...{data:payments.filter(reg=>searchFilter(reg, searchInput)&&(statutFilter==='' || reg.statut === statutFilter)),table, selectItems, setItems, rowsPerPage:25, rowsPerPageOptions:[25,50,100]}}/>
            </Box>

            <Dialog {...{open, onClose:()=>manage_dialog(false), maxWidth:action==='edit'&&user.role==='super admin' ? 'xs' : 'sm', fullWidth:true}} >
                {action === 'info' && <AdmPaymentInfo {...{payment, close:()=>manage_dialog(false)}}/>}
                {action === 'add' && <AdmPaymentForm {...{close:()=>manage_dialog(false), updatePayments: ps => setPayments(old=> [...old, ps])}}/>}
                {action === 'edit' && (user?.role === 'super admin' ? 
                    <AdmPaymentEdit {...{payment, updatePayment: p => setPayments(old => old.map(b => b._id === p._id ? p : b)), close:()=>manage_dialog(false)}} /> :
                    <AdmPaymentForm {...{payment, close:()=>manage_dialog(false), updatePayments: ps => setPayments(old=> old.map(b => b._id === ps._id ? ps : b))}}/>)
                }
            </Dialog>

            <Snackbar open={openSnackbar} autoHideDuration={5000} onClose={()=> manage_snackbar(false)} message={snackMsg} />

            <Backdrop open={load}><CircularProgress/></Backdrop>
        </AppLayout>
    )
}

